import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../api";

const PAGE_SIZE = 10;

const ROLE_LABELS = {
  super_admin: "Super Admin",
  admin:       "Admin",
  site_admin:  "Site Admin",
};

const ROLE_COLORS = {
  super_admin: { bg: "#fff0f0", color: "#8B2020", border: "#f5c6c2" },
  admin:       { bg: "#e8f4fd", color: "#004d74", border: "#6dceff" },
  site_admin:  { bg: "#f0faf4", color: "#1a6e35", border: "#8ee0aa" },
};

export default function UserManagement({ apiBase, currentUserEmail, userEmail, userGroups, signOut }) {
  const navigate = useNavigate();
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [notice, setNotice]         = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [busy, setBusy]             = useState({});
  const [search, setSearch]         = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage]             = useState(1);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${apiBase}/admin/users`);
      if (!res.ok) throw new Error("Failed to load users");
      setUsers(await res.json());
    } catch (e) {
      showNotice("error", e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => { setPage(1); }, [search, filterRole, filterStatus]);

  const showNotice = (type, text) => {
    setNotice({ type, text });
    setTimeout(() => setNotice(null), 4000);
  };

  const setBusyFor = (email, val) =>
    setBusy(prev => ({ ...prev, [email]: val }));

  // ── Filtered + paginated list ─────────────────────────────────────────────

  const filtered = users.filter(u => {
    const q = search.trim().toLowerCase();
    if (q && !u.name?.toLowerCase().includes(q) && !u.email?.toLowerCase().includes(q)) return false;
    if (filterRole) {
      if (filterRole === "__none__" ? u.groups.length > 0 : !u.groups.includes(filterRole)) return false;
    }
    if (filterStatus) {
      const uStatus = !u.enabled ? "disabled"
        : u.status === "CONFIRMED"             ? "active"
        : u.status === "FORCE_CHANGE_PASSWORD" ? "invite_sent"
        : u.status === "RESET_REQUIRED"        ? "reset_required"
        : u.status === "UNCONFIRMED"           ? "unconfirmed"
        : "other";
      if (uStatus !== filterStatus) return false;
    }
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const pageUsers  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleToggleEnabled = async (user) => {
    const action = user.enabled ? "disable" : "enable";
    if (!window.confirm(`${user.enabled ? "Disable" : "Enable"} account for ${user.email}?`)) return;
    setBusyFor(user.email, true);
    try {
      const res = await authFetch(
        `${apiBase}/admin/users/${encodeURIComponent(user.email)}/${action}`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error(await res.text());
      showNotice("ok", `User ${action}d`);
      fetchUsers();
    } catch (e) {
      showNotice("error", `Failed to ${action}: ${e.message}`);
    } finally {
      setBusyFor(user.email, false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Permanently delete user ${user.email}? This cannot be undone.`)) return;
    setBusyFor(user.email, true);
    try {
      const res = await authFetch(
        `${apiBase}/admin/users/${encodeURIComponent(user.email)}`,
        { method: "DELETE" },
      );
      if (!res.ok) throw new Error(await res.text());
      showNotice("ok", "User deleted");
      fetchUsers();
    } catch (e) {
      showNotice("error", `Failed to delete: ${e.message}`);
    } finally {
      setBusyFor(user.email, false);
    }
  };

  const handleSaveGroups = async (email, groups) => {
    setBusyFor(email, true);
    try {
      const res = await authFetch(
        `${apiBase}/admin/users/${encodeURIComponent(email)}/groups`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ groups }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      showNotice("ok", "Role updated");
      setEditingUser(null);
      fetchUsers();
    } catch (e) {
      showNotice("error", `Failed to update role: ${e.message}`);
    } finally {
      setBusyFor(email, false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>

      {/* Header — matches main admin header */}
      <header style={headerStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => navigate("/")} style={backBtn}>← Back</button>
          <div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>User Management</div>
            <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
              Signed in as {userEmail}
              {userGroups?.length > 0 && ` · ${userGroups.join(", ")}`}
            </div>
          </div>
        </div>
        <button onClick={signOut} style={outlineBtn}>Sign out</button>
      </header>

      <main style={{ maxWidth: 960, margin: "0 auto", padding: "24px 16px" }}>

        {notice && (
          <div style={{
            background: notice.type === "ok" ? "#e0f3fc" : "#fdecea",
            border: `1px solid ${notice.type === "ok" ? "#6dceff" : "#f5c6c2"}`,
            color:  notice.type === "ok" ? "#004d74" : "#8B2020",
            borderRadius: 8, padding: "10px 16px",
            marginBottom: 16, fontSize: 14,
          }}>
            {notice.text}
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
          <input
            type="search"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={searchInput}
          />
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} style={filterSelect}>
            <option value="">All roles</option>
            <option value="super_admin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="site_admin">Site Admin</option>
            <option value="__none__">No role</option>
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={filterSelect}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="invite_sent">Invite sent</option>
            <option value="reset_required">Reset required</option>
            <option value="unconfirmed">Unconfirmed</option>
            <option value="disabled">Disabled</option>
          </select>
          {(filterRole || filterStatus) && (
            <button
              onClick={() => { setFilterRole(""); setFilterStatus(""); }}
              style={clearBtn}
            >
              Clear filters
            </button>
          )}
          <div style={{ marginLeft: "auto" }}>
            <button
              onClick={() => { setShowInvite(v => !v); setEditingUser(null); }}
              style={inviteBtn(showInvite)}
            >
              {showInvite ? "Cancel" : "+ Invite user"}
            </button>
          </div>
        </div>

        {/* Invite form */}
        {showInvite && (
          <InviteForm
            apiBase={apiBase}
            onSaved={(msg) => { showNotice("ok", msg); setShowInvite(false); fetchUsers(); }}
            onCancel={() => setShowInvite(false)}
          />
        )}

        {/* Edit role form */}
        {editingUser && (
          <EditRoleForm
            user={editingUser}
            busy={busy[editingUser.email]}
            onSave={(groups) => handleSaveGroups(editingUser.email, groups)}
            onCancel={() => setEditingUser(null)}
          />
        )}

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#888", fontSize: 14 }}>
              Loading users…
            </div>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={{ background: "#f8f8f6" }}>
                      {["Name", "Email", "Role", "Status", "Actions"].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageUsers.length === 0 ? (
                      <tr>
                        <td colSpan={5} style={{ textAlign: "center", color: "#888", padding: 32, fontSize: 14 }}>
                          {search ? `No users matching "${search}"` : "No users found"}
                        </td>
                      </tr>
                    ) : (
                      pageUsers.map(u => (
                        <UserRow
                          key={u.username}
                          user={u}
                          isSelf={u.email.toLowerCase() === currentUserEmail?.toLowerCase()}
                          isBusy={!!busy[u.email]}
                          isEditing={editingUser?.email === u.email}
                          onEdit={() => setEditingUser(editingUser?.email === u.email ? null : u)}
                          onToggle={() => handleToggleEnabled(u)}
                          onDelete={() => handleDelete(u)}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={paginationBar}>
                <span style={{ fontSize: 13, color: "#888" }}>
                  {filtered.length === 0
                    ? "0 users"
                    : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, filtered.length)} of ${filtered.length} user${filtered.length !== 1 ? "s" : ""}`}
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={safePage === 1}
                    style={pageBtn(safePage === 1)}
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                    <button
                      key={n}
                      onClick={() => setPage(n)}
                      style={pageNumBtn(n === safePage)}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={safePage === totalPages}
                    style={pageBtn(safePage === totalPages)}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

      </main>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function UserRow({ user, isSelf, isBusy, isEditing, onEdit, onToggle, onDelete }) {
  const primaryGroup = user.groups[0];
  const rc = primaryGroup ? ROLE_COLORS[primaryGroup] : null;

  return (
    <tr style={{ opacity: user.enabled ? 1 : 0.55, transition: "opacity 0.15s" }}>
      <td style={tdStyle}>
        <div style={{ fontWeight: 500, fontSize: 14, color: "#1a1a1a" }}>{user.name}</div>
      </td>
      <td style={tdStyle}>
        <div style={{ fontSize: 13, color: "#555" }}>{user.email}</div>
        {isSelf && <div style={{ fontSize: 11, color: "#0093dd", marginTop: 2 }}>You</div>}
      </td>
      <td style={tdStyle}>
        {primaryGroup ? (
          <span style={{ ...badgeBase, background: rc.bg, color: rc.color, border: `1px solid ${rc.border}` }}>
            {ROLE_LABELS[primaryGroup] ?? primaryGroup}
          </span>
        ) : (
          <span style={{ ...badgeBase, background: "#f0f0f0", color: "#999" }}>No role</span>
        )}
      </td>
      <td style={tdStyle}>
        <StatusBadge status={user.status} enabled={user.enabled} />
      </td>
      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <ActionBtn onClick={onEdit}   disabled={isBusy} active={isEditing} label="Role"       color="#0093dd" />
          <ActionBtn onClick={onToggle} disabled={isBusy || isSelf} label={user.enabled ? "Disable" : "Enable"} color={user.enabled ? "#d94f2b" : "#1a9e4a"} />
          <ActionBtn onClick={onDelete} disabled={isBusy || isSelf} label="Delete" color="#8B2020" />
        </div>
      </td>
    </tr>
  );
}

function StatusBadge({ status, enabled }) {
  if (!enabled) return <span style={{ ...badgeBase, background: "#f0f0f0", color: "#888" }}>Disabled</span>;
  const map = {
    CONFIRMED:             { bg: "#f0faf4", color: "#1a6e35", border: "#8ee0aa", label: "Active" },
    FORCE_CHANGE_PASSWORD: { bg: "#fff8e6", color: "#7a5200", border: "#ffd270", label: "Invite sent" },
    RESET_REQUIRED:        { bg: "#fff8e6", color: "#7a5200", border: "#ffd270", label: "Reset required" },
    UNCONFIRMED:           { bg: "#f5f5f5", color: "#555",    border: "#ccc",    label: "Unconfirmed" },
  };
  const s = map[status] ?? { bg: "#f5f5f5", color: "#555", border: "#ccc", label: status };
  return <span style={{ ...badgeBase, background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{s.label}</span>;
}

function ActionBtn({ onClick, disabled, active, label, color }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        background: active ? color : "none",
        color:      active ? "#fff" : color,
        border:     `1px solid ${color}`,
        borderRadius: 6, padding: "3px 9px",
        fontSize: 11, fontWeight: 500,
        opacity:  disabled ? 0.35 : 1,
        cursor:   disabled ? "not-allowed" : "pointer",
        transition: "all 0.1s",
      }}
    >
      {label}
    </button>
  );
}

function InviteForm({ apiBase, onSaved, onCancel }) {
  const [name, setName]   = useState("");
  const [email, setEmail] = useState("");
  const [group, setGroup] = useState("site_admin");
  const [saving, setSaving] = useState(false);
  const [err, setErr]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr(null);
    try {
      const res = await authFetch(`${apiBase}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), group }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ detail: res.statusText }));
        throw new Error(body.detail ?? res.statusText);
      }
      onSaved("Invitation sent — user will receive a temporary password by email");
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <div style={subFormWrap}>
      <div style={subFormTitle}>Invite new user</div>
      {err && <div style={{ color: "#8B2020", fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto auto", gap: 10, alignItems: "end" }}>
          <div>
            <label style={fieldLabel}>Full name</label>
            <input type="text" placeholder="e.g. Priya Sharma" value={name}
              onChange={e => setName(e.target.value)} style={inputStyle} required />
          </div>
          <div>
            <label style={fieldLabel}>Email address</label>
            <input type="email" placeholder="user@example.com" value={email}
              onChange={e => setEmail(e.target.value)} style={inputStyle} required />
          </div>
          <div>
            <label style={fieldLabel}>Role</label>
            <select value={group} onChange={e => setGroup(e.target.value)} style={inputStyle}>
              <option value="site_admin">Site Admin</option>
              <option value="admin">Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          <button type="button" onClick={onCancel} style={cancelBtn}>Cancel</button>
          <button type="submit" disabled={saving} style={saveBtnStyle(saving)}>
            {saving ? "Sending…" : "Send invite"}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditRoleForm({ user, busy, onSave, onCancel }) {
  const [selected, setSelected] = useState(user.groups[0] ?? "");

  return (
    <div style={subFormWrap}>
      <div style={subFormTitle}>Change role — {user.email}</div>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <select value={selected} onChange={e => setSelected(e.target.value)} style={{ ...inputStyle, maxWidth: 220 }}>
          <option value="">No role</option>
          <option value="site_admin">Site Admin</option>
          <option value="admin">Admin</option>
          <option value="super_admin">Super Admin</option>
        </select>
        <button type="button" onClick={onCancel} style={cancelBtn}>Cancel</button>
        <button onClick={() => onSave(selected ? [selected] : [])} disabled={busy} style={saveBtnStyle(busy)}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const headerStyle = {
  background: "#004d74", color: "#fff",
  padding: "14px 24px", display: "flex",
  alignItems: "center", justifyContent: "space-between",
};

const backBtn = {
  background: "rgba(255,255,255,0.15)", border: "none",
  color: "#fff", borderRadius: 8, padding: "6px 14px",
  fontSize: 13, fontWeight: 500, cursor: "pointer",
};

const outlineBtn = {
  background: "transparent", border: "1px solid rgba(255,255,255,0.5)",
  color: "#fff", borderRadius: 8, padding: "8px 16px",
  fontSize: 13, fontWeight: 500, cursor: "pointer",
};

const searchInput = {
  padding: "9px 14px", borderRadius: 8,
  border: "1px solid #ddd", fontSize: 14,
  outline: "none", width: 280, boxSizing: "border-box",
  background: "#fff",
};

const filterSelect = {
  padding: "9px 12px", borderRadius: 8,
  border: "1px solid #ddd", fontSize: 13,
  outline: "none", background: "#fff", cursor: "pointer",
};

const clearBtn = {
  padding: "9px 12px", borderRadius: 8,
  background: "none", border: "1px solid #f5c6c2",
  color: "#8B2020", fontSize: 12, fontWeight: 600,
  cursor: "pointer",
};

const inviteBtn = (active) => ({
  padding: "9px 18px", borderRadius: 8,
  background: active ? "#f0f0ee" : "#0093dd",
  color: active ? "#555" : "#fff",
  border: active ? "1px solid #ddd" : "none",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
});

const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13 };

const thStyle = {
  textAlign: "left", padding: "10px 14px",
  borderBottom: "2px solid #e5e5e5",
  fontSize: 11, fontWeight: 700,
  color: "#888", textTransform: "uppercase", letterSpacing: "0.05em",
};

const tdStyle = {
  padding: "11px 14px",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
};

const badgeBase = {
  display: "inline-block", padding: "3px 9px",
  borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
};

const paginationBar = {
  display: "flex", justifyContent: "space-between", alignItems: "center",
  padding: "12px 16px", borderTop: "1px solid #f0f0f0",
};

const pageBtn = (disabled) => ({
  padding: "5px 12px", borderRadius: 6,
  border: "1px solid #ddd", background: "#fff",
  fontSize: 12, cursor: disabled ? "not-allowed" : "pointer",
  color: disabled ? "#ccc" : "#555",
});

const pageNumBtn = (active) => ({
  width: 30, height: 30, borderRadius: 6,
  border: active ? "none" : "1px solid #ddd",
  background: active ? "#0093dd" : "#fff",
  color: active ? "#fff" : "#555",
  fontSize: 12, cursor: "pointer", fontWeight: active ? 700 : 400,
});

const subFormWrap = {
  background: "#f8f8f6", borderRadius: 10,
  padding: 16, marginBottom: 16,
};

const subFormTitle = {
  fontSize: 12, fontWeight: 700, color: "#666",
  textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12,
};

const fieldLabel = {
  display: "block", fontSize: 11, fontWeight: 600,
  color: "#888", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em",
};

const inputStyle = {
  padding: "9px 12px", borderRadius: 8,
  border: "1px solid #ddd", fontSize: 14,
  outline: "none", width: "100%", boxSizing: "border-box",
};

const cancelBtn = {
  padding: "9px 14px", borderRadius: 8,
  background: "none", border: "1px solid #ccc",
  fontSize: 13, cursor: "pointer", color: "#555", whiteSpace: "nowrap",
};

const saveBtnStyle = (disabled) => ({
  padding: "9px 18px", borderRadius: 8,
  background: disabled ? "#d0d0d0" : "#0093dd",
  color: "#fff", border: "none",
  fontSize: 13, fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap",
});
